import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ForCDMOS } from '../constants';
import { ChevronDown, Building2, Rocket, Users, Zap, Shield,Target, UserPlus, Bot, MessageCircle, ClipboardCheck, Network, Handshake, LogIn } from 'lucide-react';
import {useAuth} from '../context/AuthContext';
import {dbService} from "../lib/supabase";

const HomePage: React.FC = () => {

  const [scrollY, setScrollY] = useState(0);
  const { user } = useAuth();
  const [applicationStatus, setApplicationStatus] = useState(false);
  if (user) {
    dbService.getCDMOApplicationByUserId(user.id).then((application) => {
      console.log(application);
      if(application && application.company_name && application.contact_name && application.email && application.phone && application.primary_capabilities.length>0 && application.typical_batch_sizes.length>0 ) {
        setApplicationStatus(true);
      }
      else {
        setApplicationStatus(false);
      }
    });
    console.log(user);
  }

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.8, 
        type: 'spring',
        stiffness: 100,
        damping: 15,
        ease: 'easeOut'
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        type: 'spring',
        stiffness: 80,
        damping: 12
      }
    }
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-deep-indigo via-darker-electric-blue to-darker-soft-teal">
          {/* Hero Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-lighten"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/3938023/pexels-photo-3938023.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop)'
            }}
          />
          
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at 20% 50%, rgba(79, 143, 255, 0.3) 0%, transparent 60%), 
                          radial-gradient(circle at 80% 20%, rgba(58, 61, 152, 0.2) 0%, transparent 60%)`
            }}
          />
          
          {/* Subtle animated mesh gradient overlay */}
          <div className="absolute inset-0 opacity-30">
            <div 
              className="absolute inset-0 bg-gradient-to-r from-electric-blue/20 via-transparent to-deep-indigo/20"
              style={{
                animation: 'pulse 8s ease-in-out infinite alternate'
              }}
            />
          </div>
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/10 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                ease: 'linear'
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>

        {/* Hero Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.5 }}
          className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto"
        >
          <motion.h1
            variants={itemVariants}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight"
          >
            Algorithm-Based CDMO Matching
            <br />
            <span className="bg-gradient-to-r from-lime-green to-soft-teal bg-clip-text text-transparent">
              for Biotech & Pharma
            </span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-lg sm:text-xl md:text-2xl text-white/95 mb-8 sm:mb-10 md:mb-12 max-w-3xl mx-auto leading-relaxed font-medium px-2"
          >
            Connect with the perfect manufacturing partner. 
            From small molecules to cell therapy, we match you with CDMOs that fit your needs.
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-2"
          >
              {user ? (
  <Link to="/matchmaking">
    <motion.button
      whileHover={{
        scale: 1.05,
        boxShadow: '0 15px 30px rgba(79, 143, 255, 0.4)',
        y: -2,
      }}
      whileTap={{ scale: 0.95 }}
      className="group relative px-8 sm:px-10 py-4 sm:py-5 bg-gradient-hero-cta text-white font-bold rounded-2xl text-lg sm:text-xl shadow-xl transition-all duration-300 w-full sm:w-auto sm:min-w-[220px] border-2 border-white/30 backdrop-blur-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <Rocket className="inline-block w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform relative z-10" />
      <span className="relative z-10">Find CDMOs</span>
    </motion.button>
  </Link>
) : (
  <>
  <Link to="/startup-signup">
    <motion.button
      whileHover={{
        scale: 1.05,
        boxShadow: '0 15px 30px rgba(79, 143, 255, 0.4)',
        y: -2,
      }}
      whileTap={{ scale: 0.95 }}
      className="group relative px-8 sm:px-10 py-4 sm:py-5 bg-gradient-hero-cta text-white font-bold rounded-2xl text-lg sm:text-xl shadow-xl transition-all duration-300 w-full sm:w-auto sm:min-w-[220px] border-2 border-white/30 backdrop-blur-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <Rocket className="inline-block w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform relative z-10" />
      <span className="relative z-10">Sign Up</span>
    </motion.button>
  </Link>
  <Link to="/startup-signup?mode=login">
    <motion.button
      whileHover={{
        scale: 1.05,
        boxShadow: '0 15px 30px rgba(79, 143, 255, 0.4)',
        y: -2,
      }}
      whileTap={{ scale: 0.95 }}
      className="group relative px-8 sm:px-10 py-4 sm:py-5 bg-gradient-hero-cta text-white font-bold rounded-2xl text-lg sm:text-xl shadow-xl transition-all duration-300 w-full sm:w-auto sm:min-w-[220px] border-2 border-white/30 backdrop-blur-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <LogIn className="inline-block w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform relative z-10" />
      <span className="relative z-10">Sign In</span>
    </motion.button>
  </Link>
  </>
)}

          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white/70 cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <ChevronDown className="w-6 h-6 sm:w-8 sm:h-8" />
        </motion.div>
      </section>

      {/* Combined Features and How It Works Section with Continuous Background */}
      <section className="bg-gradient-to-br from-light-gray via-sky-blue/10 to-gradient-pink-violet-start/5 relative overflow-hidden">
        {/* Features Section Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-8 mix-blend-overlay"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/3938022/pexels-photo-3938022.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop)'
          }}
        />
        
        {/* Continuous Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-electric-blue/30 to-soft-teal/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-gradient-accent opacity-30 rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-96 left-1/4 w-96 h-96 bg-gradient-to-r from-lime-green/20 to-soft-teal/20 rounded-full filter blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-32 right-1/4 w-96 h-96 bg-gradient-success opacity-20 rounded-full filter blur-3xl animate-pulse-slow" style={{ animationDelay: '4s' }}></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-gradient-to-r from-sky-blue/30 to-electric-blue/30 rounded-full mix-blend-multiply filter blur-xl animate-pulse-slow" style={{ animationDelay: '6s' }}></div>
        </div>
        
        {/* Features Section */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="py-12 sm:py-16 md:py-20 relative z-10"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: false }}
              className="text-center mb-8 sm:mb-12 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-charcoal mb-4">
                Why Choose Pharma Connect Navigator?
              </h2>
              <p className="text-lg sm:text-xl text-slate-gray max-w-3xl mx-auto font-medium px-2">
                We bridge the gap between innovative biotech companies and world-class manufacturing partners
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {[
                {
                  icon: Zap,
                  color: 'from-electric-blue to-soft-teal',
                  bgColor: 'from-sky-blue/10 to-electric-blue/5',
                  title: 'Algorithm-Based Matching',
                  description: 'Our intelligent algorithm matches you with CDMOs based on your specific needs, capabilities, and timeline.'
                },
                {
                  icon: Shield,
                  color: 'from-lime-green to-soft-teal',
                  bgColor: 'from-lime-green/10 to-soft-teal/5',
                  title: 'Extensive Partner Network',
                  description: 'Access our extensive CDMO network to find partners that fit your specific needs.'
                },
                {
                  icon: Target,
                  color: 'bg-gradient-accent',
                  bgColor: 'from-gradient-pink-violet-start/10 to-gradient-pink-violet-end/5',
                  title: 'Perfect Fit',
                  description: 'From small molecules to complex biologics, we find manufacturers that specialize in your therapeutic area.'
                }
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: false }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className={`bg-gradient-to-br ${feature.bgColor} backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/50 group`}
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4 group-hover:text-deep-indigo transition-colors">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-slate-gray leading-relaxed font-medium">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* How It Works Section */}
        <motion.div 
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="py-12 sm:py-16 relative z-10"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: false }}
              className="text-center mb-8 sm:mb-12 md:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal mb-4 sm:mb-6">
                How It Works
              </h2>
              <p className="text-lg sm:text-xl text-slate-gray max-w-3xl mx-auto font-medium leading-relaxed px-2">
                Our streamlined process connects biotech innovators with manufacturing excellence through intelligent matching
              </p>
            </motion.div>

            {/* Two-column layout for user flows */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 xl:gap-16">
              {/* Startup Flow */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: false }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-lime-green/10 to-soft-teal/10 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl border border-white/50 relative overflow-hidden">
                  {/* Background gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-success opacity-5 rounded-2xl sm:rounded-3xl"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center mb-6 sm:mb-8">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-success rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
                        <Rocket className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-charcoal">For Startups & CMC Leads</h3>
                    </div>

                    <div className="space-y-6 sm:space-y-8">
                      {[
                        {
                          icon: UserPlus,
                          title: 'Sign Up',
                          description: 'Create a profile with key project details including product type, development stage, testing needs, and manufacturing requirements.',
                          delay: 0.1
                        },
                        {
                          icon: Bot,
                          title: 'Smart Matching',
                          description: 'Our algorithm matches your needs with CDMO partners based on capabilities, availability, specialization, and mutual compatibility.',
                          delay: 0.2
                        },
                        {
                          icon: MessageCircle,
                          title: 'Request Introduction',
                          description: 'Request intros to selected CDMOs, track status in real-time, and communicate directly via our secure platform.',
                          delay: 0.3
                        }
                      ].map((step, index) => (
                        <motion.div
                          key={step.title}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: step.delay }}
                          viewport={{ once: false }}
                          whileHover={{ x: 5 }}
                          className="flex items-start space-x-4 group"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl shadow-md flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300">
                              <step.icon className="w-6 h-6 text-lime-green" />
                            </div>
                            {index < 2 && (
                              <div className="w-0.5 h-6 sm:h-8 bg-gradient-to-b from-lime-green to-soft-teal mx-auto mt-3 sm:mt-4"></div>
                            )}
                          </div>
                          <div className="flex-1 pt-1 sm:pt-2">
                            <h4 className="text-base sm:text-lg font-semibold text-charcoal mb-2 group-hover:text-lime-green transition-colors">
                              Step {index + 1}: {step.title}
                            </h4>
                            <p className="text-sm sm:text-base text-slate-gray leading-relaxed">{step.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      viewport={{ once: false }}
                      className="mt-10 pt-8 border-t border-lime-green/20"
                    >
                    {user ? (
                      <Link to="/matchmaking">
                        <motion.button
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-success text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base"
                        >
                          <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">
                            You already have an account. Find CDMOs
                          </span>
                        </motion.button>
                      </Link>
                    ) : (
                      <Link to="/startup-signup">
                        <motion.button
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-success text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base"
                        >
                          <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">
                            Create Account or Sign In
                          </span>
                        </motion.button>
                      </Link>
                    )}
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* CDMO Flow */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: false }}
                className="relative"
              >
                <div className="bg-gradient-to-br from-electric-blue/10 to-deep-indigo/10 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl border border-white/50 relative overflow-hidden">
                  {/* Background gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-primary opacity-5 rounded-2xl sm:rounded-3xl"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center mb-6 sm:mb-8">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-primary rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-charcoal">For CDMOs</h3>
                    </div>

                    <div className="space-y-6 sm:space-y-8">
                      {ForCDMOS.map((step, index) => (
                        <motion.div
                          key={step.title}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: step.delay }}
                          viewport={{ once: false }}
                          whileHover={{ x: 5 }}
                          className="flex items-start space-x-4 group"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-xl shadow-md flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300">
                              <step.icon className="w-6 h-6 text-electric-blue" />
                            </div>
                            {index < 2 && (
                              <div className="w-0.5 h-6 sm:h-8 bg-gradient-to-b from-electric-blue to-deep-indigo mx-auto mt-3 sm:mt-4"></div>
                            )}
                          </div>
                          <div className="flex-1 pt-1 sm:pt-2">
                            <h4 className="text-base sm:text-lg font-semibold text-charcoal mb-2 group-hover:text-electric-blue transition-colors">
                              Step {index + 1}: {step.title}
                            </h4>
                            <p className="text-sm sm:text-base text-slate-gray leading-relaxed">{step.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      viewport={{ once: false }}
                      className="mt-10 pt-8 border-t border-electric-blue/20"
                    >
                      {applicationStatus ? (
  // ✅ Application exists → go to matchmaking
  <Link to="/matchmaking">
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-primary text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base"
    >
      <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">
        Find CDMOs
      </span>
    </motion.button>
  </Link>
) : user ? (
  // 🚀 User exists but no application → go to profile edit
  <Link to="/cdmo-profile-edit">
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-primary text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base"
    >
      <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">
        Complete Your CDMO Profile
      </span>
    </motion.button>
  </Link>
) : (
  // 🏢 No user → go to CDMO application form
  <Link to="/cdmo-application">
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-gradient-primary text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base"
    >
      <span className="group-hover:translate-x-1 transition-transform duration-300 inline-block">
        Apply Now – Be featured as a preferred manufacturing partner
      </span>
    </motion.button>
  </Link>
)}

                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <motion.section 
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-deep-indigo via-electric-blue to-soft-teal relative overflow-hidden"
      >
        {/* CTA Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-lighten"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/2280571/pexels-photo-2280571.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop)'
          }}
        />
        
        {/* Enhanced Background decoration matching hero */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at 20% 50%, rgba(79, 143, 255, 0.3) 0%, transparent 60%), 
                          radial-gradient(circle at 80% 20%, rgba(58, 61, 152, 0.2) 0%, transparent 60%)`
            }}
          />
          
          {/* Subtle animated mesh gradient overlay matching hero */}
          <div className="absolute inset-0 opacity-30">
            <div 
              className="absolute inset-0 bg-gradient-to-r from-electric-blue/20 via-transparent to-deep-indigo/20"
              style={{
                animation: 'pulse 8s ease-in-out infinite alternate'
              }}
            />
          </div>
          
          {/* Floating particles matching hero */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/10 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, -100, 0],
                opacity: [0.3, 0.8, 0.3]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                ease: 'linear'
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        
        <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="relative z-10"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
              Ready to Find Your Perfect Manufacturing Partner?
            </h2>
            <p className="text-xl sm:text-2xl md:text-2xl text-white/95 mb-10 sm:mb-12 md:mb-14 font-medium max-w-4xl mx-auto leading-relaxed">
              Join hundreds of biotech companies that have found their ideal CDMO through our platform
            </p>
            
            {/* Enhanced CTA buttons matching hero layout */}
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center items-center">
             {user ? (
  // If user exists → Show single Dashboard button
  <Link to="/matchmaking">
    <motion.button
      whileHover={{
        scale: 1.05,
        boxShadow: '0 20px 40px rgba(79, 143, 255, 0.4)',
        y: -3,
      }}
      whileTap={{ scale: 0.95 }}
      className="group relative px-10 sm:px-12 py-5 sm:py-6 bg-gradient-hero-cta text-white font-bold rounded-2xl text-xl sm:text-2xl shadow-2xl transition-all duration-300 w-full sm:w-auto sm:min-w-[280px] border-2 border-white/30 backdrop-blur-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <span className="relative z-10">
        Find CDMOs
      </span>
    </motion.button>
  </Link>
) : (
  // If no user → Show two signup buttons
  <>
    <Link to="/startup-signup">
      <motion.button
        whileHover={{
          scale: 1.05,
          boxShadow: '0 20px 40px rgba(79, 143, 255, 0.4)',
          y: -3,
        }}
        whileTap={{ scale: 0.95 }}
        className="group relative px-10 sm:px-12 py-5 sm:py-6 bg-gradient-hero-cta text-white font-bold rounded-2xl text-xl sm:text-2xl shadow-2xl transition-all duration-300 w-full sm:w-auto sm:min-w-[280px] border-2 border-white/30 backdrop-blur-sm"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <span className="relative z-10">
          Sign Up
        </span>
      </motion.button>
    </Link>

    <Link to="/startup-signup">
      <motion.button
        whileHover={{ 
          scale: 1.05, 
          boxShadow: '0 15px 30px rgba(255, 255, 255, 0.3)',
          y: -3,
        }}
        whileTap={{ scale: 0.95 }}
        className="group relative px-8 sm:px-10 py-4 sm:py-5 bg-white/95 backdrop-blur-sm text-deep-indigo font-semibold rounded-xl text-lg sm:text-xl shadow-xl hover:bg-white hover:border-electric-blue/50 transition-all duration-300 w-full sm:w-auto sm:min-w-[240px] border-2 border-white/40"
      >
        <Building2 className="inline-block w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
        Apply as a CDMO
      </motion.button>
    </Link>
  </>
)}

            </div>
            
            {/* Additional value proposition */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: false }}
              className="mt-12 sm:mt-16"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
                {[
                  {
                    icon: Zap,
                    title: 'Algorithm-Based',
                    description: 'Smart matching algorithm'
                  },
                  {
                    icon: Shield,
                    title: 'Extensive Partner Network',
                    description: 'Diverse range of CDMOs'
                  },
                  {
                    icon: Target,
                    title: 'Perfect Fit',
                    description: 'Specialized expertise'
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: false }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                      <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-white/80 font-medium">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default HomePage;
